import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { ErrorPage } from '~/chords/ErrorPage';
import { useUser } from '~/modules/auth/hooks/useUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { CatalogAnomalyDashboardPage } from '~/modules/marketing/pages/catalog-anomaly-dashboard/CatalogAnomalyDashboardPage';
import { AnomalyDetectionPage } from '~/modules/marketing/pages/catalog-anomaly-dashboard/pages/anomaly-detection/AnomalyDetectionPage';
import { KeyMomentsPage } from '~/modules/marketing/pages/catalog-anomaly-dashboard/pages/key-moments/KeyMomentsPage';
import { ShortTermForecastingPage } from '~/modules/marketing/pages/catalog-anomaly-dashboard/pages/short-term-forecasting/ShortTermForecastingPage';
import { WMG1_APP_NAME } from '~/modules/user-management/constants';

import { AutomatedCampaignsPage } from '../pages/automated-campaigns/AutomatedCampaignsPage';
import { AutomatedCreatorCampaignPage } from '../pages/automated-creator-campaign/AutomatedCreatorCampaignPage';
import { CreateCampaignPage } from '../pages/automated-creator-campaign/pages/CreateCampaignPage/CreateCampaignPage';
import { ReviewCampaignPage } from '../pages/automated-creator-campaign/pages/ReviewCampaignPage/ReviewCampaignPage';
import { DigitalCampaignPerformancePage } from '../pages/digital-campaign-performance/DigitalCampaignPerformancePage';
import { MarketingPage } from '../pages/MarketingPage/MarketingPage';

/**
 * Top-level router combining module-level routers
 */
export function Router() {
  const [isAutomatedCreatorCampaignsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgAutomatedCreatorCampaignsEnabled,
  ]);
  const { hasPermission } = useUser();
  const permissionToViewAutomatedCreatorCampaigns = hasPermission(
    'MC3.VIEW',
    undefined,
    WMG1_APP_NAME,
  );
  const permissionToCreateAutomatedCreatorCampaigns = hasPermission(
    'MC3.CREATE',
    undefined,
    WMG1_APP_NAME,
  );

  return (
    <Suspense>
      <Routes>
        <Route element={<Outlet />}>
          <Route index element={<MarketingPage />} />
          <Route
            path="campaigns"
            element={<DigitalCampaignPerformancePage />}
          />
          <Route
            path="automated-campaigns"
            element={<AutomatedCampaignsPage />}
          />
          {isAutomatedCreatorCampaignsEnabled ? (
            <Route path="automated-creator-campaigns" element={<Outlet />}>
              <Route
                index
                element={
                  permissionToViewAutomatedCreatorCampaigns ? (
                    <AutomatedCreatorCampaignPage />
                  ) : (
                    <ErrorPage
                      type="noAccessPage"
                      message="Please contact your administrator to grant you permission to view automated creator campaigns."
                    />
                  )
                }
              />
              <Route
                path="new"
                element={
                  permissionToCreateAutomatedCreatorCampaigns ? (
                    <CreateCampaignPage />
                  ) : (
                    <ErrorPage
                      type="noAccessPage"
                      message="Please contact your administrator to grant you permission to create automated creator campaigns."
                    />
                  )
                }
              />
              <Route
                path="new/review"
                element={
                  permissionToCreateAutomatedCreatorCampaigns ? (
                    <ReviewCampaignPage />
                  ) : (
                    <ErrorPage
                      type="noAccessPage"
                      message="Please contact your administrator to grant you permission to create automated creator campaigns."
                    />
                  )
                }
              />
            </Route>
          ) : (
            <Route
              path="automated-creator-campaigns/*"
              element={
                <ErrorPage
                  type="noAccessPage"
                  message="You do not have permission to view or create automated creator campaigns."
                />
              }
            />
          )}

          <Route path="catalog" element={<CatalogAnomalyDashboardPage />}>
            <Route
              index
              element={<Navigate to="anomaly-detection" replace />}
            />
            <Route
              path="anomaly-detection"
              element={<AnomalyDetectionPage />}
            />

            <Route path="key-moments" element={<KeyMomentsPage />} />

            <Route
              path="short-term-forecasting"
              element={<ShortTermForecastingPage />}
            />
          </Route>

          <Route path="*" element={<ErrorPage type="notFoundPage" />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

Router.displayName = 'MarketingRouter';
